import { Toast } from "primereact/toast";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PurpleButton } from "../../components/buttons/purpleButton";
import { CarrouselContainer, TextWrapper } from "../../components/carrousel/styles";
import { ImageUpload } from "../../components/imageUpload";
import { TextBox } from "../../components/textBox";
import { useUploadingState } from "../../hooks/useUploadingState";
import { ICarrossel } from "../../interfaces/model/iCarrossel";
import carrosselService from "../../services/carrosselService";
import { withImageFallback } from "../../utils/imageFallback";
import { scrollUp } from "../../utils/pageUtils";
import {
  Actions,
  Container,
  Content,
  Form,
  Header
} from "./styles";

const INITIAL_STATE: ICarrossel = {
  titulo: "",
  texto: "",
  imagem: "",
};

const TITULO_MAX_LENGTH = 240;
const TEXTO_MAX_LENGTH = 240;
type ToastType = "error" | "success" | "warn";
const TOAST_SUMMARIES: Record<ToastType, string> = {
  error: "Erro",
  success: "Sucesso",
  warn: "Atenção",
};

export const GestaoCarrossel = () => {
  const toast = useRef<Toast>(null);

  const [carrosseis, setCarrosseis] = useState<ICarrossel[]>([]);
  const [selectedId, setSelectedId] = useState<string>("");
  const [form, setForm] = useState<ICarrossel>(INITIAL_STATE);
  const { isUploading, uploadingRef, onUploadingChange } =
    useUploadingState();

  const selectedItem = useMemo(() => {
    if (!selectedId) return undefined;
    return carrosseis.find((item) => String(item.id) === selectedId);
  }, [selectedId, carrosseis]);

  const loadCarrossel = useCallback(() => {
    carrosselService
      .getAll()
      .then((res) => {
        setCarrosseis(res.data);
      })
      .catch(() => {
        toastMessage("Falha ao carregar itens do carrossel.", "error");
      });
  }, []);

  useEffect(() => {
    scrollUp();
    loadCarrossel();
  }, [loadCarrossel]);

  const toastMessage = (message: string, type: ToastType) => {
    toast.current?.show({
      severity: type,
      summary: TOAST_SUMMARIES[type],
      detail: message,
      life: 3000,
    });
  };

  const handleSelectChange = (value: string) => {
    if (uploadingRef.current) {
      toastMessage("Aguarde o envio da imagem terminar.", "warn");
      return;
    }

    setSelectedId(value);

    if (!value) {
      setForm(INITIAL_STATE);
      return;
    }

    const item = carrosseis.find((it) => String(it.id) === value);
    if (item) {
      setForm({
        titulo: item.titulo || "",
        texto: item.texto || "",
        imagem: item.imagem || "",
      });
    }
  };

  const handleSave = () => {
    if (uploadingRef.current) {
      toastMessage("Aguarde o envio da imagem terminar.", "warn");
      return;
    }

    if (!form.titulo || !form.texto || !form.imagem) {
      toastMessage("Preencha título, texto e imagem.", "error");
      return;
    }

    if (selectedItem?.id) {
      carrosselService
        .update(selectedItem.id, form)
        .then(() => {
          toastMessage("Carrossel atualizado com sucesso.", "success");
          loadCarrossel();
        })
        .catch(() => {
          toastMessage("Falha ao atualizar carrossel.", "error");
        });

      return;
    }

    carrosselService
      .create(form)
      .then(() => {
        toastMessage("Item do carrossel criado com sucesso.", "success");
        setSelectedId("");
        setForm(INITIAL_STATE);
        loadCarrossel();
      })
      .catch(() => {
        toastMessage("Falha ao criar item do carrossel.", "error");
      });
  };

  const handleDelete = () => {
    if (uploadingRef.current) {
      toastMessage("Aguarde o envio da imagem terminar.", "warn");
      return;
    }

    if (!selectedItem?.id) {
      return;
    }

    carrosselService
      .remove(selectedItem.id)
      .then(() => {
        toastMessage("Item do carrossel excluído com sucesso.", "success");
        setSelectedId("");
        setForm(INITIAL_STATE);
        loadCarrossel();
      })
      .catch(() => {
        toastMessage("Falha ao excluir item do carrossel.", "error");
      });
  };

  return (
    <>
      <Toast ref={toast} />
      <Container>
        <Content>
          <Header>
            <h1>Gestão do Carrossel</h1>
            <select
              aria-label="Item do carrossel"
              disabled={isUploading}
              value={selectedId}
              onChange={(e) => handleSelectChange(e.target.value)}
            >
              <option value="">Novo item</option>
              {carrosseis.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.titulo}
                </option>
              ))}
            </select>
          </Header>

          <Form>
            <input
              aria-label="Título do carrossel"
              placeholder="Título"
              value={form.titulo}
              maxLength={TITULO_MAX_LENGTH}
              onChange={(e) => setForm((prev) => ({ ...prev, titulo: e.target.value }))}
            />
            <TextBox
              title="Texto"
              value={form.texto}
              onChangeText={(text) => setForm((prev) => ({ ...prev, texto: text }))}
              height="180px"
              maxLength={TEXTO_MAX_LENGTH}
            />
            <ImageUpload
              label="Imagem"
              required
              category="CARROSSEL"
              contentId={selectedItem?.id}
              value={form.imagem}
              onChange={(url) => setForm((prev) => ({ ...prev, imagem: url }))}
              onUploadingChange={onUploadingChange}
            />
          </Form>

            <CarrouselContainer>
              <TextWrapper>
                <h2>{form.titulo || "Título do carrossel"}</h2>
                <h3>{form.texto || "Texto de pré-visualização do carrossel."}</h3>
              </TextWrapper>
              <img
                src={withImageFallback(form.imagem)}
                alt={form.titulo || "Pré-visualização"}
              />
            </CarrouselContainer>
          

          <Actions>
            {selectedItem?.id && (
              <PurpleButton
                title="Excluir item"
                onClick={handleDelete}
                disabled={isUploading}
              />
            )}
            <PurpleButton
              title={selectedItem?.id ? "Atualizar item" : "Criar item"}
              onClick={handleSave}
              disabled={isUploading}
            />
          </Actions>
        </Content>
      </Container>
    </>
  );
};
